import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3, Beaker, Link as LinkIcon, Loader2, MessageSquare, RotateCcw } from "lucide-react";
import useAdminAuth, { adminAuthHeader, getAdminToken } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAG_GROUPS = {
  "R1 · No password (A/B/C)": [
    "msg_pwd_a1", "msg_pwd_a2", "msg_pwd_a3", "msg_pwd_a4",
    "msg_pwd_b1", "msg_pwd_b2", "msg_pwd_b3", "msg_pwd_b4",
    "msg_pwd_c1", "msg_pwd_c2", "msg_pwd_c3", "msg_pwd_c4",
  ],
  "R2 · L1 non avviata · vergine (A/B)": [
    "msg_l1_a_a1", "msg_l1_a_a2", "msg_l1_a_a3", "msg_l1_a_a4",
    "msg_l1_a_b1", "msg_l1_a_b2", "msg_l1_a_b3", "msg_l1_a_b4",
  ],
  "R3 · L1 non avviata · già toccato": ["msg_l1_b"],
  "R4-R6 · L1 <80%": [
    "msg_fb_a_1", "msg_fb_a_2", "msg_fb_a_3",
    "msg_fb_b_1", "msg_fb_b_2",
    "msg_fb_c1", "msg_fb_c2",
  ],
  "R7 · L1 80% · vergine": ["msg_l2_1", "msg_l2_2", "msg_l2_3", "msg_l2_4"],
  "R8-R9 · L1 80% · già toccato": [
    "msg_l2_fb_1", "msg_l2_fb_2",
    "msg_l2_pwd_1", "msg_l2_pwd_2",
  ],
  "R10-R11 · L2 richiesta": ["msg_l2_req_a", "msg_l2_req_b1", "msg_l2_req_b2"],
};

const TAG_LABELS = {
  msg_pwd_a1: "R1 · A · 1/4 (0s)", msg_pwd_a2: "R1 · A · 2/4 (+10s)", msg_pwd_a3: "R1 · A · 3/4 (+25s) · 🔗", msg_pwd_a4: "R1 · A · 4/4 (+29s)",
  msg_pwd_b1: "R1 · B · 1/4 (0s)", msg_pwd_b2: "R1 · B · 2/4 (+10s)", msg_pwd_b3: "R1 · B · 3/4 (+25s) · 🔗", msg_pwd_b4: "R1 · B · 4/4 (+29s)",
  msg_pwd_c1: "R1 · C · 1/4 (0s)", msg_pwd_c2: "R1 · C · 2/4 (+10s)", msg_pwd_c3: "R1 · C · 3/4 (+25s) · 🔗", msg_pwd_c4: "R1 · C · 4/4 (+29s)",
  msg_l1_a_a1: "R2 · A · 1/4 (0s)", msg_l1_a_a2: "R2 · A · 2/4 (+10s)", msg_l1_a_a3: "R2 · A · 3/4 (+25s) · 🔗", msg_l1_a_a4: "R2 · A · 4/4 (+29s)",
  msg_l1_a_b1: "R2 · B · 1/4 (0s)", msg_l1_a_b2: "R2 · B · 2/4 (+10s)", msg_l1_a_b3: "R2 · B · 3/4 (+25s) · 🔗", msg_l1_a_b4: "R2 · B · 4/4 (+29s)",
  msg_l1_b: "R3 · single · 🔗",
  msg_fb_a_1: "R4 · 1/3 (0s)", msg_fb_a_2: "R4 · 2/3 (+10s)", msg_fb_a_3: "R4 · 3/3 (+20s) · 🔗",
  msg_fb_b_1: "R5 · 1/2 (0s)", msg_fb_b_2: "R5 · 2/2 (+10s) · 🔗",
  msg_fb_c1: "R6 · 1/2 (0s)", msg_fb_c2: "R6 · 2/2 (+30s)",
  msg_l2_1: "R7 · 1/4 (0s)", msg_l2_2: "R7 · 2/4 (+10s)", msg_l2_3: "R7 · 3/4 (+20s) · 🔗 bundle", msg_l2_4: "R7 · 4/4 (+23s)",
  msg_l2_fb_1: "R8 · 1/2 (0s)", msg_l2_fb_2: "R8 · 2/2 (+10s) · 🔗 bundle",
  msg_l2_pwd_1: "R9 · 1/2 (0s)", msg_l2_pwd_2: "R9 · 2/2 (+10s) · 🔗 bundle",
  msg_l2_req_a: "R10 · single (caldo)",
  msg_l2_req_b1: "R11 · 1/2 (0s)", msg_l2_req_b2: "R11 · 2/2 (+15s)",
};

export default function AdminAutomationPage() {
  const { authed, verifying } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (verifying || !authed) return;
    setLoading(true);
    axios.get(`${API}/admin/automation/overview`, { headers: adminAuthHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [verifying, authed, refreshTick]);

  const tagMap = useMemo(() => {
    const m = {};
    (data?.templates || []).forEach((t) => { m[t.tag] = t; });
    return m;
  }, [data]);

  if (verifying) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" size={28} style={{ color: "var(--np-secondary)" }} />
      </main>
    );
  }
  if (!authed || !getAdminToken()) {
    return (
      <main className="min-h-screen flex items-center justify-center p-8">
        <p>Sessione admin non valida — <Link to="/admin/dashboard" className="underline">vai al login</Link>.</p>
      </main>
    );
  }
  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" size={28} style={{ color: "var(--np-secondary)" }} />
      </main>
    );
  }

  const abR1 = data?.ab_test_r1 || { A: {}, B: {}, C: {} };
  const abR2 = data?.ab_test_r2 || { A: {}, B: {} };
  const tl = data?.tracked_links || {};
  const r1Winner = (() => {
    const arr = ["A", "B", "C"].map((v) => [v, abR1[v]?.rate_pct ?? 0]).sort((a, b) => b[1] - a[1]);
    return arr[0][1] > 0 && arr[0][1] !== arr[1][1] ? arr[0][0] : null;
  })();
  const r2Winner = (abR2.A?.rate_pct ?? 0) > (abR2.B?.rate_pct ?? 0) ? "A"
    : (abR2.B?.rate_pct ?? 0) > (abR2.A?.rate_pct ?? 0) ? "B" : null;

  return (
    <main className="min-h-screen" style={{ background: "var(--np-tertiary)" }} data-testid="admin-automation-page">
      <header className="border-b" style={{ borderColor: "#dbe3ec", background: "#fff" }}>
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--np-primary)" }}>
            <ArrowLeft size={15} /> Dashboard
          </Link>
          <h1 className="text-lg font-semibold" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
            Automazioni · Follow-up engine
          </h1>
          <button
            type="button"
            onClick={() => setRefreshTick((n) => n + 1)}
            data-testid="automation-refresh"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border"
            style={{ borderColor: "#cbd5e1", color: "#475569", background: "#fff" }}
          >
            <RotateCcw size={13} /> Aggiorna
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8 grid gap-6">
        {/* A/B/C test card — R1 (no-password drip) */}
        <section
          className="rounded-2xl p-6 border bg-white"
          style={{ borderColor: "#dbe3ec" }}
          data-testid="ab-test-r1-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <Beaker size={18} style={{ color: "var(--np-secondary)" }} />
            <h2 className="text-base font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--np-primary)" }}>
              A/B/C test R1 · drip &laquo;no password&raquo;
            </h2>
          </div>
          <p className="text-[12px] mb-4" style={{ color: "#64748b" }}>
            Conversione = il lead ha impostato la password dopo aver ricevuto il drip.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["A", "B", "C"].map((variant) => {
              const v = abR1[variant] || {};
              const isWinner = r1Winner === variant;
              const copyHint = {
                A: 'Opener "201° genitore" upfront',
                B: 'Opener corto, riprende "201°" nel msg 2',
                C: 'Pattern interrupt: "qual\'è quella cosa..."',
              }[variant];
              return (
                <div
                  key={variant}
                  data-testid={`ab-r1-card-${variant}`}
                  className="rounded-xl p-5 border-2"
                  style={{
                    borderColor: isWinner ? "var(--np-secondary)" : "#e2e8f0",
                    background: isWinner ? "rgba(213,93,57,0.04)" : "#f8fafc",
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: isWinner ? "var(--np-secondary)" : "#64748b" }}>
                      Variante {variant} {isWinner && "🏆"}
                    </p>
                    <span className="text-2xl font-semibold tabular-nums" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
                      {v.rate_pct ?? 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide" style={{ color: "#64748b" }}>Assegnati</p>
                      <p className="text-lg font-semibold tabular-nums" style={{ color: "var(--np-primary)" }}>{v.assigned ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide" style={{ color: "#64748b" }}>Pwd impostata</p>
                      <p className="text-lg font-semibold tabular-nums" style={{ color: "#16a34a" }}>{v.converted ?? 0}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-[12px]" style={{ color: "#64748b" }}>{copyHint}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* A/B test card — R2 (L1 not started drip) */}
        <section
          className="rounded-2xl p-6 border bg-white"
          style={{ borderColor: "#dbe3ec" }}
          data-testid="ab-test-r2-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <Beaker size={18} style={{ color: "var(--np-secondary)" }} />
            <h2 className="text-base font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--np-primary)" }}>
              A/B test R2 · drip &laquo;L1 non avviata&raquo;
            </h2>
          </div>
          <p className="text-[12px] mb-4" style={{ color: "#64748b" }}>
            Conversione = il lead ha avviato la lezione 1 dopo aver ricevuto il drip.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["A", "B"].map((variant) => {
              const v = abR2[variant] || {};
              const isWinner = r2Winner === variant;
              const copyHint = {
                A: 'Msg 2 enfatizza "201° genitore"',
                B: 'Msg 2 sobrio: "Molti dei genitori..."',
              }[variant];
              return (
                <div
                  key={variant}
                  data-testid={`ab-r2-card-${variant}`}
                  className="rounded-xl p-5 border-2"
                  style={{
                    borderColor: isWinner ? "var(--np-secondary)" : "#e2e8f0",
                    background: isWinner ? "rgba(213,93,57,0.04)" : "#f8fafc",
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: isWinner ? "var(--np-secondary)" : "#64748b" }}>
                      Variante {variant} {isWinner && "🏆"}
                    </p>
                    <span className="text-2xl font-semibold tabular-nums" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
                      {v.rate_pct ?? 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide" style={{ color: "#64748b" }}>Assegnati</p>
                      <p className="text-lg font-semibold tabular-nums" style={{ color: "var(--np-primary)" }}>{v.assigned ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide" style={{ color: "#64748b" }}>L1 avviata</p>
                      <p className="text-lg font-semibold tabular-nums" style={{ color: "#16a34a" }}>{v.converted ?? 0}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-[12px]" style={{ color: "#64748b" }}>{copyHint}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Tracked links card */}
        <section
          className="rounded-2xl p-6 border bg-white"
          style={{ borderColor: "#dbe3ec" }}
          data-testid="tracked-links-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <LinkIcon size={18} style={{ color: "var(--np-secondary)" }} />
            <h2 className="text-base font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--np-primary)" }}>
              Link sblocco gratuiti
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Generati" value={tl.total ?? 0} />
            <Stat label={`Cliccati (${tl.click_rate_pct ?? 0}%)`} value={tl.clicked ?? 0} color="#2563eb" />
            <Stat label={`Sbloccati (${tl.claim_rate_pct ?? 0}% dei cliccati)`} value={tl.claimed ?? 0} color="#16a34a" />
          </div>
        </section>

        {/* Templates breakdown */}
        <section
          className="rounded-2xl p-6 border bg-white"
          style={{ borderColor: "#dbe3ec" }}
          data-testid="templates-card"
        >
          <div className="flex items-center gap-2 mb-5">
            <MessageSquare size={18} style={{ color: "var(--np-secondary)" }} />
            <h2 className="text-base font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--np-primary)" }}>
              Template per regola
            </h2>
          </div>
          <div className="space-y-5">
            {Object.entries(TAG_GROUPS).map(([group, tags]) => (
              <div key={group} className="rounded-lg p-4" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: "var(--np-secondary)" }}>{group}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left" style={{ color: "#64748b" }}>
                        <th className="font-semibold pr-3 py-1">Template</th>
                        <th className="font-semibold text-right px-2 py-1 tabular-nums">Pending</th>
                        <th className="font-semibold text-right px-2 py-1 tabular-nums">Inviati</th>
                        <th className="font-semibold text-right px-2 py-1 tabular-nums">Skip</th>
                        <th className="font-semibold text-right px-2 py-1 tabular-nums">Fail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tags.map((tag) => {
                        const r = tagMap[tag] || {};
                        return (
                          <tr key={tag} data-testid={`template-row-${tag}`} className="border-t" style={{ borderColor: "#e2e8f0" }}>
                            <td className="pr-3 py-1.5" style={{ color: "var(--np-primary)" }}>
                              {TAG_LABELS[tag] || tag}
                              <span className="ml-2 text-[10px] font-mono" style={{ color: "#94a3b8" }}>{tag}</span>
                            </td>
                            <td className="text-right px-2 py-1.5 tabular-nums font-semibold" style={{ color: "#0284c7" }}>{r.pending || 0}</td>
                            <td className="text-right px-2 py-1.5 tabular-nums font-semibold" style={{ color: "#16a34a" }}>{r.sent || 0}</td>
                            <td className="text-right px-2 py-1.5 tabular-nums" style={{ color: "#64748b" }}>{r.skipped || 0}</td>
                            <td className="text-right px-2 py-1.5 tabular-nums font-semibold" style={{ color: (r.failed || 0) > 0 ? "#dc2626" : "#94a3b8" }}>{r.failed || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Skip reasons */}
        {data?.skip_reasons?.length > 0 && (
          <section
            className="rounded-2xl p-6 border bg-white"
            style={{ borderColor: "#dbe3ec" }}
            data-testid="skip-reasons-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18} style={{ color: "var(--np-secondary)" }} />
              <h2 className="text-base font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--np-primary)" }}>
                Motivi di skip (top 10)
              </h2>
            </div>
            <ul className="space-y-1.5 text-sm">
              {data.skip_reasons.map((r) => (
                <li key={r.reason} className="flex items-center justify-between border-b pb-1.5" style={{ borderColor: "#eef1f5" }}>
                  <code className="text-[12px]" style={{ color: "#475569" }}>{r.reason}</code>
                  <span className="font-semibold tabular-nums" style={{ color: "var(--np-primary)" }}>{r.n}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}

function Stat({ label, value, color = "var(--np-primary)" }) {
  return (
    <div className="text-center p-4 rounded-lg" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
      <p className="text-2xl md:text-3xl font-semibold tabular-nums" style={{ color, fontFamily: "Merriweather, serif" }}>
        {value}
      </p>
      <p className="text-[11px] mt-1 uppercase tracking-wide" style={{ color: "#64748b" }}>{label}</p>
    </div>
  );
}
